import React from 'react';
import { find } from 'lodash';
import * as yup from 'yup';
import { useFieldsConfig } from './form-config/field-config';
import { useMaterialServiceSchema } from './form-config/useMaterialServiceSchema';
import { useWaterAddedSchema } from './form-config/useWaterAddedSchema';
import timestampSchema from './TimestampSchema';
import { useCompanySetup, useVehicleSetup, useVehicleTypeSetup, useReasonCodeSetup } from './MasterDataProvider';
import { useTicketContext } from './TicketContext';

export const useFormConfig = (
  loadedQuantity,
  vehicleId,
  airCompliance,
  consistenceCompliance,
  airTempCompliance,
  concreteTempCompliance,
  cylindersCompliance,
  primaryLineItem = null
) => {
  const materialServiceSchema = useMaterialServiceSchema(loadedQuantity);
  const waterAddedSchema = useWaterAddedSchema(loadedQuantity);
  const { data: company } = useCompanySetup();
  const vehicleSetup = useVehicleSetup();
  const vehicleTypeSetup = useVehicleTypeSetup();
  const { ticket } = useTicketContext();
  const reasonCodeSetup = useReasonCodeSetup();

  const vehicleTypeData = React.useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: vehicleId });
    return find(vehicleTypeSetup.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
  }, [vehicleSetup.data, vehicleTypeSetup.data, vehicleId]);

  const validationSchema = yup.object().shape({
    lineItems: yup.array().of(materialServiceSchema),
    waterAddedEvents: yup.array().of(waterAddedSchema),
    driverDidNotAdd: yup.boolean(),
    returnConcreteOnTruck: yup.object().when('driverDidNotAdd', {
      is: driverDidNotAdd =>
        !!company?.isReturnedMaterialMandatory &&
        driverDidNotAdd !== true &&
        (!!vehicleTypeData?.isReturnedMaterialInWorkflow || !vehicleTypeData) &&
        ticket?.customData?.ticketType !== 'PMP',
      then: yup.object().shape({
        value: yup
          .number()
          .max(loadedQuantity.value, {
            key: 'exceedsLoadedQuantity',
            values: { loadedQuantity: loadedQuantity.value },
          })
          .required('required'),
      }),
      otherwise: yup.object().shape({
        value: yup.number().max(loadedQuantity.value, {
          key: 'exceedsLoadedQuantity',
          values: { loadedQuantity: loadedQuantity.value },
        }),
      }),
    }),
    returnReason: yup.object().when('returnConcreteOnTruck', {
      is: returnConcreteOnTruck => {
        const optionsReason =
          reasonCodeSetup?.data
            ?.filter(reason => reason?.type === 'RETURNED')
            ?.filter(reason => reason?.status === 'ACTIVE')
            ?.filter(reason =>
              company?.isMulticountry === true
                ? reason?.countries?.includes(ticket?.origin?.address?.countryCode) || !reason?.countries?.length
                : reason
            )
            ?.filter(reason => reason?.vehicleTypes?.includes(vehicleTypeData) || !reason?.vehicleTypes?.length) || [];
        return returnConcreteOnTruck?.value > 0 && optionsReason?.length > 0;
      },
      then: schema => schema.required('required'),
    }),
    ticketEvents: yup.object().shape({
      PRINTED: timestampSchema('PRINTED'),
      LOADING_STARTED: timestampSchema('LOADING_STARTED'),
      LOADING_COMPLETE: timestampSchema('LOADING_COMPLETE'),
      TO_JOB: timestampSchema('TO_JOB'),
      ARRIVE_JOB: timestampSchema('ARRIVE_JOB'),
      UNLOADING: timestampSchema('UNLOADING'),
      END_UNLOADING: timestampSchema('END_UNLOADING'),
      LEAVE_JOB: timestampSchema('LEAVE_JOB'),
      IN_YARD: timestampSchema('IN_YARD'),
      READY_TO_PUMP: timestampSchema('READY_TO_PUMP'),
      PUMPING_STARTED: timestampSchema('PUMPING_STARTED'),
      PUMPING_COMPLETE: timestampSchema('PUMPING_COMPLETE'),
    }),
    productCompliance: yup.object().shape({
      consistenceValue: yup.object().shape({
        value: yup
          .number()
          .max(consistenceCompliance?.maximumValue, {
            key: 'maxValueError',
            values: { maxQty: consistenceCompliance?.maximumValue },
          })
          .min(consistenceCompliance?.minimumValue, {
            key: 'minValueError',
            values: { minQty: consistenceCompliance?.minimumValue },
          }),
      }),
      airContentValue: yup.object().shape({
        value: yup
          .number()
          .max(airCompliance?.maximumValue, {
            key: 'maxValueError',
            values: { maxQty: airCompliance?.maximumValue },
          })
          .min(airCompliance?.minimumValue, {
            key: 'minValueError',
            values: { minQty: airCompliance?.minimumValue },
          }),
      }),
      airTempValue: !!airTempCompliance
        ? yup.object().shape({
            value: yup
              .number()
              .max(airTempCompliance?.maximumValue, {
                key: 'maxValueError',
                values: { maxQty: airTempCompliance?.maximumValue },
              })
              .min(airTempCompliance?.minimumValue, {
                key: 'minValueError',
                values: { minQty: airTempCompliance?.minimumValue },
              }),
          })
        : yup.object().shape({
            value: yup.number(),
          }),
      concreteTempValue: !!concreteTempCompliance
        ? yup.object().shape({
            value: yup
              .number()
              .max(concreteTempCompliance?.maximumValue, {
                key: 'maxValueError',
                values: { maxQty: concreteTempCompliance?.maximumValue },
              })
              .min(concreteTempCompliance?.minimumValue, {
                key: 'minValueError',
                values: { minQty: concreteTempCompliance?.minimumValue },
              }),
          })
        : yup.object().shape({
            value: yup.number(),
          }),
      cylinderNumber: !!cylindersCompliance
        ? yup.object().shape({
            value: yup
              .number()
              .max(cylindersCompliance?.maximumValue, {
                key: 'maxValueError',
                values: { maxQty: cylindersCompliance?.maximumValue },
              })
              .min(cylindersCompliance?.minimumValue, {
                key: 'minValueError',
                values: { minQty: cylindersCompliance?.minimumValue },
              }),
          })
        : yup.object().shape({
            value: yup.number(),
          }),
    }),
  });

  const fields = useFieldsConfig(loadedQuantity, primaryLineItem);

  return {
    validationSchema,
    fields,
  };
};
